import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IAccountWithBalance, useAccountsWithBalances } from 'entities/cex-account';
import { useTypedSelector } from 'store';
import { usePairConfig } from 'store/slices/pair_configs/hooks';
import { ICexAccount } from 'types/accounts';
import { EChartFormat, IChartRange, IChartTimeframe, IPriceFormat } from 'types/charts';

interface IChartsControlsContext {
  period: { get: IChartRange | undefined; set: Dispatch<SetStateAction<IChartRange | undefined>> };
  startDate: { get: number | undefined; set: Dispatch<SetStateAction<number | undefined>> };
  endDate: { get: number | undefined; set: Dispatch<SetStateAction<number | undefined>> };
  selectedAccounts: {
    get: IAccountWithBalance[];
    set: Dispatch<SetStateAction<ICexAccount[]>>;
  };

  timeframe: {
    get: IChartTimeframe;
    set: Dispatch<SetStateAction<IChartTimeframe>>;
  };
  chartFormat: EChartFormat;
  setChartFormat: Dispatch<SetStateAction<EChartFormat>>;
  priceFormat: IPriceFormat;
  setPriceFormat: Dispatch<SetStateAction<IPriceFormat>>;
  balancesShow: Record<string, boolean>;
  setBalancesShow: Dispatch<SetStateAction<Record<string, boolean>>>;
  showActiveOrders: boolean;
  setShowActiveOrders: (v: boolean) => void;
  selectedBots: {
    get: string[];
    set: Dispatch<SetStateAction<string[]>>;
  };
  balanceSmoothing: {
    get: boolean;
    set: Dispatch<SetStateAction<boolean>>;
  };
  resetBalancesShow: () => void;
  accountsToAPI: (selectedAccounts: ICexAccount[]) => {
    array: number[];
    optional?: number[];
  };
  selectedChart: {
    get: 'Original' | 'Trading';
    set: (v: 'Original' | 'Trading') => void;
  };
}

export const ChartsControlsContext = createContext<IChartsControlsContext>({
  period: { get: 'ALL', set: () => {} },
  startDate: { get: 0, set: () => {} },
  endDate: { get: 0, set: () => {} },
  selectedAccounts: { get: [], set: () => {} },
  balancesShow: {},
  setBalancesShow: () => {},
  showActiveOrders: true,
  setShowActiveOrders: () => {},
  selectedBots: { get: [], set: () => {} },
  balanceSmoothing: { get: true, set: () => {} },
  resetBalancesShow: () => {},
  timeframe: { get: '1m', set: () => {} },
  chartFormat: EChartFormat.Normal,
  setChartFormat: () => {},
  priceFormat: 'token',
  setPriceFormat: () => {},
  accountsToAPI: () => ({ array: [] }),
  selectedChart: { get: 'Original', set: () => {} },
});

export const ChartsControlsContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair)!;

  const { accounts } = useAccountsWithBalances({
    pairId: cexPair?.id,
    cex: cexPair?.cex,
    symbol: cexPair ? `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}` : '',
    enabled: Boolean(cexPair),
  });

  const [selectedChart, setSelectedChart] = useState<'Original' | 'Trading'>('Trading');

  const [period, setPeriod] = useState<IChartRange | undefined>('1D');
  const [startDate, setStartDate] = useState<number | undefined>(undefined);
  const [endDate, setEndDate] = useState<number | undefined>(undefined);

  const [selectedAccounts, setSelectedAccounts] = useState<ICexAccount[]>([]);

  const [timeframe, setTimeframe] = useState<IChartTimeframe>('5m');
  const [chartFormat, setChartFormat] = useState<EChartFormat>(EChartFormat.Percentage);
  const [priceFormat, setPriceFormat] = useState<IPriceFormat>('token');

  const INITIAL_BALANCES_SHOW = useMemo(
    () => ({
      [cexPair.token_base.symbol]: true,
      [cexPair.token_quote.symbol]: true,
      [cexPair.token_native.symbol]: true,
      Total: true,
    }),
    [cexPair],
  );

  const { pairConfig, setPairConfig } = usePairConfig({ id: cexPair.id });

  const [balancesShow, setBalancesShow] = useState<Record<string, boolean>>(INITIAL_BALANCES_SHOW);
  const [showActiveOrders, setShowActiveOrders] = useState<boolean>(
    pairConfig.showActiveOrders ?? true,
  );
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [balanceSmoothingEnabled, setBalanceSmoothingEnabled] = useState<boolean>(false);
  const hasSyncedInitialStateRef = useRef(false);
  // Capture initial pairConfig.showActiveOrders value to avoid stale closure
  const initialShowActiveOrdersRef = useRef(pairConfig.showActiveOrders);

  // Synchronize showActiveOrders with selectedBots on initialization
  // If no bots are selected and showActiveOrders is true (default), hide orders to maintain consistent state
  // Only sync once on mount and only if showActiveOrders wasn't explicitly saved in pairConfig
  useEffect(() => {
    if (hasSyncedInitialStateRef.current) {
      return;
    }

    if (
      selectedBots.length === 0 &&
      showActiveOrders &&
      initialShowActiveOrdersRef.current === undefined
    ) {
      setShowActiveOrders(false);
      hasSyncedInitialStateRef.current = true;
    } else {
      hasSyncedInitialStateRef.current = true;
    }
  }, [selectedBots.length, showActiveOrders]);

  useEffect(() => {
    setPairConfig(cexPair.id, { ...pairConfig, showActiveOrders });

    //eslint-disable-next-line
  }, [cexPair, setPairConfig, showActiveOrders]);

  useEffect(() => {
    const currentState = pairConfig.cexBalanceSmoothing?.[cexPair.cex];
    if (currentState === balanceSmoothingEnabled) {
      return;
    }

    setPairConfig(cexPair.id, {
      ...pairConfig,
      cexBalanceSmoothing: {
        ...(pairConfig.cexBalanceSmoothing ?? {}),
        [cexPair.cex]: balanceSmoothingEnabled,
      },
    });

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSmoothingEnabled, cexPair]);

  useEffect(() => {
    const storedValue = pairConfig.cexBalanceSmoothing?.[cexPair.cex];
    setBalanceSmoothingEnabled(storedValue ?? true);
  }, [cexPair, pairConfig.cexBalanceSmoothing]);

  const resetBalancesShow = useCallback(() => {
    setBalancesShow(INITIAL_BALANCES_SHOW);
  }, [setBalancesShow, INITIAL_BALANCES_SHOW]);

  const accountsToAPI = useCallback(
    (selectedAccounts: ICexAccount[]) => {
      const optional =
        selectedAccounts.length === accounts.length ? undefined : selectedAccounts.map(el => el.id);
      const array =
        optional === undefined || optional.length === 0 ? accounts.map(el => el.id) : optional;
      return { array, optional };
    },
    [accounts],
  );

  return (
    <ChartsControlsContext.Provider
      value={{
        period: { get: period, set: setPeriod },
        startDate: { get: startDate, set: setStartDate },
        endDate: { get: endDate, set: setEndDate },
        selectedAccounts: { get: selectedAccounts, set: setSelectedAccounts },
        balancesShow,
        setBalancesShow,
        resetBalancesShow,
        showActiveOrders,
        setShowActiveOrders,
        selectedBots: { get: selectedBots, set: setSelectedBots },
        balanceSmoothing: {
          get: balanceSmoothingEnabled,
          set: setBalanceSmoothingEnabled,
        },
        timeframe: { get: timeframe, set: setTimeframe },
        chartFormat,
        setChartFormat,
        priceFormat,
        setPriceFormat,
        accountsToAPI,
        selectedChart: { get: selectedChart, set: setSelectedChart },
      }}
    >
      {children}
    </ChartsControlsContext.Provider>
  );
};
